import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight } from 'lucide-react';
interface HeaderProps {
  onOpenDemo: () => void;
}
export function Header({
  onOpenDemo
}: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navLinks = [{
    name: 'Platform',
    href: '/platform'
  }, {
    name: 'Solutions',
    href: '/solutions'
  }, {
    name: 'Why ROI',
    href: '/why-roi'
  }, {
    name: 'Resources',
    href: '/resources'
  }, {
    name: 'Contact',
    href: '/contact'
  }];
  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);
  // Prevent scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMobileMenuOpen]);
  return <>
      <header className="fixed top-0 left-0 right-0 z-[60] bg-midnight-navy/95 backdrop-blur-md border-b border-white/10 px-6 py-4" data-id="element-129">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-130">
          <div className="flex items-center gap-12" data-id="element-131">
            <Link to="/" className="relative z-[70]" data-id="element-132">
              <img src="https://cdn.magicpatterns.com/uploads/2PmeGfc8FfeehVwkDn5EB7/ChatGPT_Image_Jul_7,_2026,_03_43_37_PM.png" alt="ROI Logo" className="h-10 md:h-14 w-auto object-contain" data-id="element-133" />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8" data-id="element-134">
              {navLinks.map(link => {
              const isActive = location.pathname === link.href;
              return <Link key={link.name} to={link.href} className={`relative text-sm font-medium transition-all duration-300 ${isActive ? 'text-stark-white' : 'text-stark-white/60 hover:text-stark-white'}`} data-id="element-135">
                    {link.name}
                    {isActive && <motion.div layoutId="navUnderline" className="absolute -bottom-1 left-0 right-0 h-0.5 bg-accent-red" transition={{
                  type: 'spring',
                  stiffness: 380,
                  damping: 30
                }} data-id="element-136" />}
                  </Link>;
            })}
            </nav>
          </div>

          <div className="flex items-center gap-4" data-id="element-137">
            <button onClick={onOpenDemo} className="hidden md:block bg-accent-red text-stark-white px-6 py-2 rounded-full text-sm font-bold hover:bg-red-700 transition-all shadow-lg shadow-accent-red/20 active:scale-95" data-id="element-138">
              Book a Demo
            </button>

            {/* Mobile Menu Toggle */}
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="md:hidden relative z-[70] p-2 text-stark-white hover:bg-white/10 rounded-lg transition-colors" aria-label="Toggle Menu" data-id="element-139">
              {isMobileMenuOpen ? <X className="w-6 h-6" data-id="element-140" /> : <Menu className="w-6 h-6" data-id="element-141" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence data-id="element-142">
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[50] md:hidden bg-midnight-navy" data-id="element-143">
            <div className="flex flex-col h-full pt-32 pb-12 px-8" data-id="element-144">
              <nav className="flex flex-col gap-8" data-id="element-145">
                {navLinks.map((link, idx) => {
              const isActive = location.pathname === link.href;
              return <motion.div key={link.name} initial={{
                opacity: 0,
                x: -20
              }} animate={{
                opacity: 1,
                x: 0
              }} transition={{
                delay: idx * 0.1
              }} data-id="element-146">
                      <Link to={link.href} className={`text-4xl font-manrope font-bold flex items-center justify-between group ${isActive ? 'text-accent-red' : 'text-stark-white'}`} data-id="element-147">
                        {link.name}
                        <ArrowRight className={`w-8 h-8 transition-transform duration-300 ${isActive ? 'translate-x-0 opacity-100' : '-translate-x-4 opacity-0 group-hover:translate-x-0 group-hover:opacity-100'}`} data-id="element-148" />
                      </Link>
                    </motion.div>;
            })}
              </nav>

              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5
          }} className="mt-auto" data-id="element-149">
                <button onClick={() => {
              setIsMobileMenuOpen(false);
              onOpenDemo();
            }} className="w-full bg-accent-red text-stark-white py-5 rounded-2xl text-xl font-bold shadow-2xl shadow-accent-red/20 active:scale-[0.98] transition-transform" data-id="element-150">
                  Book a Demo
                </button>
                <div className="mt-8 flex justify-center gap-6 text-stark-white/40 text-sm font-medium" data-id="element-151">
                  <span data-id="element-152">© 2026 ROI Intelligence</span>
                </div>
              </motion.div>
            </div>

            {/* Background Decorative Elements */}
            <div className="absolute top-1/4 -right-20 w-64 h-64 bg-accent-red/10 rounded-full blur-3xl pointer-events-none" data-id="element-153" />
            <div className="absolute bottom-1/4 -left-20 w-64 h-64 bg-accent-red/5 rounded-full blur-3xl pointer-events-none" data-id="element-154" />
          </motion.div>}
      </AnimatePresence>
    </>;
}